import React from 'react';
import { createRoot } from 'react-dom/client';
import { ReactLocation, Router, Outlet } from 'react-location';
import { rankRoutes } from 'react-location-rank-routes';
import { useTranslation } from 'react-i18next';
import { QueryClient, QueryClientProvider, setLogger } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { ToastContainer } from 'react-toastify';
import Loader from 'Core/components/Loader';
import DialogContainer from 'Core/components/Dialog/Container';
import { ErrorBoundary } from 'Core/components/ErrorBoundary';
import { getAppConfig } from 'Core/config/appConfig';
import { queryLogger } from 'Core/utils/queryLogger';
import { routes } from './routes';
import './i18n';
import 'react-toastify/dist/ReactToastify.css';

setLogger(queryLogger);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 0,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

const location = new ReactLocation();

function PaymentOfficeApp() {
  const { t } = useTranslation();
  const fallback = <Loader message={t('status.loading.component')} />;

  return (
    <QueryClientProvider client={queryClient}>
      <ReactQueryDevtools initialIsOpen={false} />
      <DialogContainer />
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover
        limit={3}
        theme="colored"
      />
      <ErrorBoundary>
        <React.Suspense fallback={fallback}>
            <Router
              location={location}
              routes={routes[getAppConfig().country]}
              filterRoutes={rankRoutes}
            >
              <Outlet />
            </Router>
        </React.Suspense>
      </ErrorBoundary>
    </QueryClientProvider>
  );
}

createRoot(document.getElementById('app-container')).render(
  <React.Suspense fallback={<Loader message="Loading translations" />}>
    <PaymentOfficeApp className="font-poppins" />
  </React.Suspense>
);
